import { useEffect, useState } from 'react'
import { ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Alert, Tag } from 'antd'
import { z } from 'zod'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'

const searchSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(100).optional(),
  status: z.enum(['pendingApproval', 'active', 'rejected', 'suspended']).optional()
})
export function RoleAccounts({ roleId }: { roleId: string }) {
  const trpc = useTRPC()
  const { session } = useAccess()
  const accountId = session?.id ?? 0
  const [persistence] = useState(() => createListQueryPersistence(`super-mega:roles:${roleId}:accounts`, searchSchema))
  const [search, setSearch] = useState(() => persistence.store.getState().searches[accountId] ?? searchSchema.parse({}))
  useEffect(() => persistence.store.getState().remember(accountId, search), [persistence, accountId, search])
  const query = useQuery({
    ...trpc.authorization.roleAccounts.queryOptions({ roleId, ...search }),
    placeholderData: keepPreviousData,
    enabled: !!session?.isSuperAdmin
  })
  type Account = NonNullable<typeof query.data>['data'][number]
  const columns: ProColumns<Account>[] = [
    { dataIndex: 'id', title: '账号 ID', copyable: true, valueType: 'digit' },
    { dataIndex: 'keyword', title: '账号 / 姓名', hideInTable: true },
    { dataIndex: 'email', title: '账号', search: false, copyable: true },
    { dataIndex: 'memberName', title: '成员', search: false },
    {
      dataIndex: 'status',
      title: '账号状态',
      valueType: 'select',
      valueEnum: { pendingApproval: '待审批', active: '活跃', rejected: '已拒绝', suspended: '停用' },
      render: (_, row) => (
        <Tag color={row.status === 'active' && !row.memberDisabled ? 'success' : 'default'}>
          {row.status === 'active'
            ? '活跃'
            : row.status === 'suspended'
              ? '停用'
              : row.status === 'rejected'
                ? '已拒绝'
                : '待审批'}
          {row.memberDisabled ? ' / 成员停用' : ''}
        </Tag>
      )
    },
    { dataIndex: 'assignedAt', title: '分配时间', valueType: 'dateTime', search: false }
  ]
  return (
    <>
      {query.isError && <Alert type="error" showIcon title={query.error.message} />}
      <ProTable<Account>
        rowKey="id"
        columns={columns}
        dataSource={query.data?.data ?? []}
        loading={query.isLoading || query.isFetching}
        form={{ initialValues: search }}
        search={{ labelWidth: 'auto' }}
        columnsState={{ persistenceKey: 'super-mega:role-accounts:columns', persistenceType: 'localStorage' }}
        onSubmit={(values) => setSearch(searchSchema.parse({ ...search, ...values, current: 1 }))}
        onReset={() => setSearch(searchSchema.parse({}))}
        options={{ reload: () => query.refetch() }}
        pagination={{
          ...search,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          onChange: (current, pageSize) => setSearch((previous) => ({ ...previous, current, pageSize }))
        }}
      />
    </>
  )
}
